/**
 * The watch's vocabulary (AA phase 4; `design/gt-logic.js:499-538`): the
 * seven triggers, which of them a game can carry, the price floor, and the
 * sentence a NOTICE BOARD row prints for an event. Pure — evaluated from the
 * store, never eyeballed from the prototype.
 */

import type { BoardEvent, EventKind, Game, TriggerKey, WatchRec } from './types.ts';

export interface TriggerMeta {
  k: TriggerKey;
  /** the matrix column label */
  l: string;
  /** the modal's row label, and every tooltip's name for it */
  full: string;
  /** the modal's one-line gloss */
  sub: string;
}

/** `GT_LOGIC.TR` — fixed matrix order. */
export const TR: readonly TriggerMeta[] = [
  { k: 'ea', l: 'EA EXIT', full: 'LEAVES EARLY ACCESS', sub: 'THE DAY IT LEAVES EARLY ACCESS' },
  { k: 'date', l: 'RELEASE DATE', full: 'RELEASE DATE SET / MOVED', sub: 'A RELEASE DATE IS SET — OR THE ONE WE HAVE MOVES' },
  { k: 'price', l: 'PRICE', full: 'PRICE BELOW MY NUMBER', sub: 'ANY TIME IT DROPS UNDER YOUR NUMBER' },
  { k: 'sale', l: 'SALE', full: 'ANY SALE', sub: 'EVERY DISCOUNT, HOWEVER SMALL' },
  { k: 'demo', l: 'DEMO', full: 'DEMO GOES LIVE', sub: 'A PLAYABLE DEMO APPEARS' },
  { k: 'upd', l: 'UPDATE', full: '1.0 / MAJOR UPDATE', sub: '1.0, A NUMBERED BUILD, OR A PATCH THE DEVS CALL MAJOR' },
  { k: 'dlc', l: 'DLC', full: 'NEW DLC', sub: 'NEW PAID DLC IS LISTED' },
];

export const TRIGGER_KEYS: readonly TriggerKey[] = TR.map((t) => t.k);

/** The board's tag per kind (`GT Mine.dc.html:597`). */
export const TAG: Readonly<Record<TriggerKey, string>> = {
  ea: 'EA EXIT',
  date: 'DATE',
  price: 'PRICE',
  sale: 'SALE',
  demo: 'DEMO',
  upd: 'UPDATE',
  dlc: 'DLC',
};

export const EMPTY_WATCH: Readonly<WatchRec> = Object.freeze({
  ea: false,
  date: false,
  price: false,
  sale: false,
  demo: false,
  upd: false,
  dlc: false,
  cap: 6,
});

/**
 * Why a trigger cannot apply to this game — `''` when it can. A dash in the
 * matrix is this, never an off switch. Only FACTS about the game rule a
 * trigger out (`trNa`, `gt-logic.js:511-521`); never a guess about what you
 * want. `upd` always applies.
 */
export function trNa(g: Game, t: TriggerKey): string {
  switch (t) {
    case 'ea':
      return g.ea ? '' : 'NOT IN EARLY ACCESS — NOTHING TO LEAVE';
    case 'date':
      return g.up ? '' : 'ALREADY OUT — THE DATE IS BEHIND US';
    case 'price':
    case 'sale':
      return g.up || g.p === null ? 'NO PRICE YET — NOTHING TO DISCOUNT' : g.p === 0 ? 'FREE — NOTHING TO DISCOUNT' : '';
    case 'demo':
      return g.demo ? 'THE DEMO IS ALREADY LIVE — GO PLAY IT' : '';
    case 'dlc':
      return g.up ? 'NOT RELEASED YET — NO DLC TO LIST' : '';
    default:
      return '';
  }
}

/** The default floor: 60% of list — a −40% sale — never under $1. */
export function capFor(g: Game | null): number {
  return Math.max(1, Math.floor((g && g.p ? g.p : 10) * 0.6));
}

/** `−40%` · `ABOVE LIST` · `''` when the game has no price. */
export function capPct(g: Game | null, cap: number): string {
  if (!g || !g.p) return '';
  const pct = Math.round((1 - cap / g.p) * 100);
  return pct <= 0 ? 'ABOVE LIST' : '−' + pct + '%';
}

/** Triggers that are armed AND apply to the game. */
export function armedKeys(rec: WatchRec | undefined, g: Game | null): TriggerKey[] {
  if (!rec || !g) return [];
  return TRIGGER_KEYS.filter((k) => rec[k] && trNa(g, k) === '');
}

export function armedN(rec: WatchRec | undefined, g: Game | null): number {
  return armedKeys(rec, g).length;
}

/** Anything armed at all — the row exists (the CHECK) iff this is true. */
export function anyArmed(rec: WatchRec): boolean {
  return TRIGGER_KEYS.some((k) => rec[k]);
}

/** The BACKLOG caption's watch line: `EA EXIT · UNDER $6`, or `''`. */
export function watchLine(rec: WatchRec | undefined, g: Game | null): string {
  return armedKeys(rec, g)
    .map((k) => (k === 'price' ? 'UNDER $' + (rec ? rec.cap : 0) : TAG[k]))
    .join(' · ');
}

const money = (v: unknown): string => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? '$' + n.toFixed(2) : '';
};

const ymText = (ym: unknown): string => {
  if (typeof ym !== 'string' || !/^\d{4}-\d{2}$/.test(ym)) return '';
  const M = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const m = Number(ym.slice(5, 7));
  return (M[m - 1] ?? '') + ' ' + ym.slice(0, 4);
};

/**
 * The sentence a board row prints. Sentence case — the board speaks like a
 * person (the design's rows: "Half off: $4.99.", "The free demo went live.").
 * PARTY rows name the actor; the caller passes their display name.
 */
export function eventText(kind: EventKind, payload: Record<string, unknown>, actorName = ''): string {
  switch (kind) {
    case 'score': {
      const v = payload.v;
      const prev = payload.prev;
      return prev === undefined || prev === null
        ? actorName + ' scored it ' + String(v) + '.'
        : actorName + ' re-scored it ' + String(v) + ', from ' + String(prev) + '.';
    }
    case 'ea':
      return 'Left early access.';
    case 'date': {
      const ym = ymText(payload.ym);
      const prev = ymText(payload.prev);
      return prev ? 'The release date moved to ' + ym + ', from ' + prev + '.' : 'A release date is set: ' + ym + '.';
    }
    case 'price':
      return money(payload.p) + ' — under the $' + String(payload.cap) + ' you set.';
    case 'sale': {
      const p = typeof payload.p === 'number' ? payload.p : Number(payload.p);
      const o = typeof payload.old === 'number' ? payload.old : Number(payload.old);
      const pct = o > 0 && p >= 0 ? Math.round((1 - p / o) * 100) : 0;
      return pct === 50 ? 'Half off: ' + money(p) + '.' : 'On sale −' + pct + '%: ' + money(p) + '.';
    }
    case 'demo':
      return 'The free demo went live.';
    case 'upd': {
      const t = typeof payload.title === 'string' ? payload.title.trim() : '';
      return t ? t + ' shipped.' : 'A major update shipped.';
    }
    case 'dlc': {
      const n = typeof payload.n === 'number' ? payload.n : Number(payload.n) || 1;
      return n === 1 ? 'A new DLC is listed on the store page.' : n + ' new DLCs are listed on the store page.';
    }
    default:
      return '';
  }
}

/** The board's tag colours: accent for the three that cost money, info for the rest. */
export function tagClr(kind: TriggerKey): string {
  return kind === 'ea' || kind === 'price' || kind === 'sale' ? 'var(--acc)' : 'var(--gt-info)';
}

export function tagBg(kind: TriggerKey): string {
  return kind === 'ea' || kind === 'price' || kind === 'sale' ? 'var(--accSoft)' : 'var(--gt-infoSoft)';
}

export function isWorld(e: BoardEvent): e is BoardEvent & { kind: TriggerKey } {
  return e.kind !== 'score';
}
